import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import router from '../../router'
import { KioskRepository } from './repository'
import { getVisitor } from './embed'
import type {
    ActiveCourseResponse,
    BetType,
    BetTypeOption,
    Course,
    GenyPronosticResponse,
    KioskBet,
    KioskTenant,
    NextCourse,
    Partant,
    PronoMemo,
    SelectedHorse,
} from './type'

export const MIN_STAKE = 2
export const MAX_STAKE = 9999
export const QUICK_STAKES = [5, 10, 20, 50]

export const useKiosk = defineStore('kiosk', () => {
    const repository = new KioskRepository()

    // --- État réunion / course ---
    const loading = ref(false)
    const isOpen = ref(false)
    const windowMinutes = ref(25)
    const reunion = ref<string | null>(null)
    const reunionId = ref<string | null>(null)
    const date = ref<string | null>(null)
    const course = ref<Course | null>(null)
    const nextCourse = ref<NextCourse | null>(null)
    const message = ref<string | null>(null)
    const tenant = ref<KioskTenant | null>(null)

    // Types de paris disponibles pour la course en cours (référentiel filtré côté API :
    // croisement paris autorisés par l'hippodrome × nombre de partants effectifs).
    const availableBetTypes = ref<BetTypeOption[]>([])

    // --- État du projet de pari ---
    const betType = ref<BetType | null>(null)
    const selectedHorses = ref<Partant[]>([])
    const stake = ref<number>(MIN_STAKE)
    const generatedBet = ref<KioskBet | null>(null)
    const printerConnected = ref<boolean | null>(null)

    // --- Pronostics Geny ---
    const genyPronostic = ref<GenyPronosticResponse | null>(null)

    // Horloge réactive : permet aux getters de se recalculer quand le temps passe.
    const nowMs = ref(Date.now())

    let pollTimer: ReturnType<typeof setInterval> | null = null
    let clockTimer: ReturnType<typeof setInterval> | null = null
    let printerTimer: ReturnType<typeof setInterval> | null = null

    // --- Getters ---

    /** Option de pari actuellement sélectionnée (métadonnées complètes). */
    const selectedBetTypeOption = computed<BetTypeOption | null>(
        () => availableBetTypes.value.find((o) => o.value === betType.value) ?? null,
    )

    /** Nombre de chevaux à sélectionner pour le type de pari courant. */
    const maxHorses = computed(() => selectedBetTypeOption.value?.horses_count ?? 1)

    /** Vrai si le type de pari courant nécessite un ordre exact. */
    const isOrdered = computed(() => selectedBetTypeOption.value?.is_ordered ?? false)

    /** Vrai si le type de pari courant demande plusieurs chevaux. */
    const isMulti = computed(() => maxHorses.value > 1)

    /**
     * La fenêtre de pari est ouverte tant que le backend l'autorise ET que
     * l'heure de départ de la course n'est pas encore atteinte.
     * Réactif via `nowMs` pour se fermer automatiquement à la fin du timer.
     */
    const bettingOpen = computed(() => {
        if (!isOpen.value || !course.value) return false
        const departMs = course.value.heure_depart_ms
        if (typeof departMs === 'number' && departMs > 0) {
            return nowMs.value < departMs
        }
        return true
    })

    const selectionComplete = computed(() => {
        if (!betType.value) return false
        return selectedHorses.value.length === maxHorses.value
    })

    /**
     * Minutes restantes avant le départ de la course en cours.
     * Sert de « compte à rebours pour aller au guichet » sur les écrans
     * d'impression et de remerciement. `null` si l'heure de départ est inconnue.
     */
    const minutesUntilDepart = computed<number | null>(() => {
        const departMs = course.value?.heure_depart_ms
        if (typeof departMs !== 'number' || departMs <= 0) return null
        return Math.max(0, Math.ceil((departMs - nowMs.value) / 60_000))
    })

    const runners = computed<Partant[]>(() => course.value?.partants ?? [])

    const availableRunners = computed<Partant[]>(() =>
        runners.value.filter((p) => !p.non_partant),
    )

    /**
     * Retourne le mémo Geny (FAVORI, OPPOSANT, OUTSIDER, DÉLAISSÉ)
     * d'un cheval donné, ou null s'il n'y a pas de pronostic disponible.
     */
    function horsePronoMemo(numero: number | string): PronoMemo | null {
        const p = genyPronostic.value
        if (!p) return null
        const n = Number(numero)
        if (p.favoris?.some((h) => h.numeroCheval === n)) return 'FAVORI'
        if (p.opposants?.some((h) => h.numeroCheval === n)) return 'OPPOSANT'
        if (p.outsiders?.some((h) => h.numeroCheval === n)) return 'OUTSIDER'
        if (p.delaisses?.some((h) => h.numeroCheval === n)) return 'DÉLAISSÉ'
        return null
    }

    /** Récupère les pronostics Geny pour la course active via le back. */
    async function fetchGenyPronostic() {
        const res = await repository.getGenyPronostic()
        genyPronostic.value = res.error ? null : (res.data as GenyPronosticResponse)
    }

    // --- Actions réunion ---
    async function fetchActiveCourse() {
        loading.value = true

        try {
            const res = await repository.getActiveCourse()

            if (!res.error) {
                const data = res.data as ActiveCourseResponse
                isOpen.value = data.is_open
                windowMinutes.value = data.window_minutes ?? 25
                reunion.value = data.reunion ?? null
                reunionId.value = data.reunion_id ?? null
                date.value = data.date ?? null
                course.value = data.course ?? null
                nextCourse.value = data.next_course ?? null
                message.value = data.message ?? null
                tenant.value = data.tenant ?? null
                availableBetTypes.value = data.available_bet_types ?? []

                // Si le pari en cours n'est plus disponible (ex: un cheval passe non-partant
                // et fait descendre le nombre de partants sous le seuil), on le réinitialise.
                if (betType.value && !availableBetTypes.value.some((o) => o.value === betType.value)) {
                    resetBet()
                }

                if (!data.is_open) {
                    const currentRoute = router.currentRoute.value.name
                    if (currentRoute !== 'home' && currentRoute !== 'setup') {
                        void router.push({ name: 'home' })
                    }
                }
            }

            return res
        } finally {
            loading.value = false
        }
    }

    function startPolling(intervalMs = 10_000) {
        stopPolling()
        pollTimer = setInterval(fetchActiveCourse, intervalMs)
        // Tick d'horloge : ferme la fenêtre de pari en direct quand le timer expire.
        clockTimer = setInterval(() => {
            nowMs.value = Date.now()
        }, 1_000)
    }

    async function checkPrinterConnection() {
        const res = await repository.getPrinterStatus()

        if (!res.error && res.data) {
            printerConnected.value = !!res.data.connected
        } else {
            printerConnected.value = false
        }

        return res
    }

    function startPrinterPolling(intervalMs = 1 * 60 * 1000) {
        stopPrinterPolling()
        void checkPrinterConnection()
        printerTimer = setInterval(() => {
            void checkPrinterConnection()
        }, intervalMs)
    }

    function stopPrinterPolling() {
        if (printerTimer) {
            clearInterval(printerTimer)
            printerTimer = null
        }
    }

    function stopPolling() {
        if (pollTimer) {
            clearInterval(pollTimer)
            pollTimer = null
        }
        if (clockTimer) {
            clearInterval(clockTimer)
            clockTimer = null
        }
        stopPrinterPolling()
    }

    // --- Actions projet de pari ---
    function selectBetType(type: BetType) {
        betType.value = type
        selectedHorses.value = []
    }

    function isHorseSelected(numero: number | string): boolean {
        return selectedHorses.value.some((h) => h.numero === numero)
    }

    function horseRank(numero: number | string): number | null {
        const index = selectedHorses.value.findIndex((h) => h.numero === numero)
        return index === -1 ? null : index + 1
    }

    function toggleHorse(partant: Partant) {
        if (partant.non_partant) return

        // Pari à un seul cheval : la sélection remplace la précédente.
        if (maxHorses.value === 1) {
            selectedHorses.value = [partant]
            return
        }

        // Pari multiple : sélection ordonnée (l'ordre est conservé pour les paris
        // « ordre exact » ; il est ignoré côté gain pour les paris désordonnés).
        if (isHorseSelected(partant.numero)) {
            selectedHorses.value = selectedHorses.value.filter((h) => h.numero !== partant.numero)
        } else if (selectedHorses.value.length < maxHorses.value) {
            selectedHorses.value = [...selectedHorses.value, partant]
        }
    }

    function setStake(value: number) {
        stake.value = Math.max(MIN_STAKE, value)
    }

    function incStake(step = 1) {
        setStake(stake.value + step)
    }

    function decStake(step = 1) {
        setStake(stake.value - step)
    }

    async function submitBet() {
        if (!betType.value || !reunionId.value || !course.value || !selectionComplete.value) {
            return { error: true, data: null }
        }

        // Sécurité : refuse le pari si la fenêtre est fermée (timer expiré).
        if (!bettingOpen.value) {
            return { error: true, data: null, closed: true }
        }

        const horses: SelectedHorse[] = selectedHorses.value.map((h) => ({
            numero: h.numero,
            cheval: h.cheval,
            jockey: h.jockey,
            entraineur: h.entraineur,
            cote: h.cote,
            casaque_url: h.casaque_url,
            badge: h.badge,
            prono_memo: horsePronoMemo(h.numero),
        }))

        const res = await repository.storeBet({
            bet_type: betType.value,
            reunion_id: reunionId.value,
            reunion_date: date.value,
            course_num_ordre: course.value.num_ordre,
            course_name: course.value.nom,
            stake: stake.value,
            horses,
            visitor: getVisitor(),
        })

        if (!res.error) {
            generatedBet.value = res.data.data as KioskBet
        }

        return res
    }

    /** Réinitialise le projet de pari (retour accueil / annulation / inactivité). */
    function resetBet() {
        betType.value = null
        selectedHorses.value = []
        stake.value = MIN_STAKE
        generatedBet.value = null
    }

    /** Envoie le ticket de pari courant (QR Code) par email au parieur connecté. */
    async function emailBet(email: string) {
        if (!generatedBet.value) {
            return { error: true, data: null }
        }
        return repository.emailBet(generatedBet.value.id, email)
    }

    return {
        // state
        loading,
        isOpen,
        windowMinutes,
        reunion,
        reunionId,
        date,
        course,
        nextCourse,
        message,
        tenant,
        betType,
        selectedHorses,
        stake,
        generatedBet,
        printerConnected,
        nowMs,
        availableBetTypes,
        genyPronostic,
        // getters
        selectedBetTypeOption,
        maxHorses,
        isOrdered,
        isMulti,
        bettingOpen,
        selectionComplete,
        minutesUntilDepart,
        runners,
        availableRunners,
        // actions
        fetchActiveCourse,
        fetchGenyPronostic,
        startPolling,
        startPrinterPolling,
        stopPolling,
        stopPrinterPolling,
        checkPrinterConnection,
        selectBetType,
        isHorseSelected,
        horseRank,
        horsePronoMemo,
        toggleHorse,
        setStake,
        incStake,
        decStake,
        submitBet,
        resetBet,
        emailBet,
    }
})
